import { useEffect, useState } from "react";
import { useAuthContext } from "../context/authContext";
import de from "../i18n/de";
import en from "../i18n/en";
import es from "../i18n/es";
import fr from "../i18n/fr";
import it from "../i18n/it";
import { LanguageCode } from "../types";

export type Translation = typeof en;
export type TranslationKeys = keyof Translation;

export const ts: { [key in LanguageCode]: Translation } = {
  en,
  it,
  de,
  es,
  fr,
  ar: en,
  cs: en,
  da: en,
  el: en,
  et: en,
  fi: en,
  hr: en,
  hu: en,
  is: en,
  ja: en,
  lt: en,
  lv: en,
  nl: en,
  no: en,
  pl: en,
  pt: en,
  ru: en,
  sk: en,
  sl: en,
  sv: en,
  tr: en,
  zh: en,
  kr: en,
};

export type I18n = {
  translation: Translation;
  errorsMap: Map<number, string>;
};

const useI18n = (): I18n => {
  const auth = useAuthContext();
  const [translation, setTranslation] = useState<Translation>(ts[auth.identity?.language ?? en.languageCode]);

  useEffect(() => {
    auth.identity?.language && setTranslation(ts[auth.identity.language] ?? en);
  }, [auth.identity?.language]);

  const errorsMap = new Map<number, string>([
    // projects
    [100001, translation.projects.errors.deleteProjectWithConfigurationsNotPossible],
    [100002, translation.projects.errors.notFound],
    [100004, translation.projects.errors.sharingNotFound],
    [100020, translation.projects.errors.sharingAlreadyExists],
    [100005, translation.projects.errors.documentNotFound],
    [100006, translation.projects.errors.missingDocumentName],
    [100007, translation.projects.errors.documentAlreadyPresent],
    [100008, translation.projects.errors.missingProjectStatus],
    [100009, translation.projects.errors.deleteProjectWithDocumentsNotPossible],

    // cables
    [200001, translation.cables.errors.notFound],
    [200002, translation.cables.errors.alreadyPresent],
    [200003, translation.cables.errors.noDefined],
    [200004, translation.cables.errors.inUseByConfiguration],

    // users
    [300001, translation.users.errors.notFound],

    // systems
    [400001, translation.systems.errors.notFound],
    [400005, translation.systems.errors.inUseByConfiguration],

    // wallMaterials
    [500001, translation.wallMaterials.errors.notFound],
    [500004, translation.wallMaterials.errors.inUseByConfiguration],

    // configurations
    [600001, translation.configurations.errors.notFound],
    [600003, translation.configurations.errors.missingNormative],
    [600004, translation.configurations.errors.missingSteelVersion],
    [600005, translation.configurations.errors.missingUpperSupportInstallationType],
    [600006, translation.configurations.errors.missingUpperSupport],
    [600007, translation.configurations.errors.missingAbsorber],
    [600008, translation.configurations.errors.missingSystemType],
    [600009, translation.configurations.errors.missingCable],
    [600020, translation.configurations.errors.alreadyLinkedToProject],
    [600022, translation.configurations.errors.notLinkedToProject],

    // systemTypes
    [700001, translation.systemTypes.errors.notFound],
    [700005, translation.systemTypes.errors.inUseByConfiguration],
    [700006, translation.systemTypes.errors.inUseByShuttle],
    [700007, translation.systemTypes.errors.inUseByIntermediateElement],

    // steelVersions
    [800001, translation.steelVersions.errors.notFound],
    [800005, translation.steelVersions.errors.inUseByConfiguration],
    [800006, translation.steelVersions.errors.inUseByUpperExtension],
    [800007, translation.steelVersions.errors.inUseByLowerElement],
    [800008, translation.steelVersions.errors.inUseByIntermediateElement],
    [800009, translation.steelVersions.errors.inUseByAsorber],
    [800010, translation.steelVersions.errors.inUseBySupport],
    [800011, translation.steelVersions.errors.inUseByCurvesIntermediate],
    [800012, translation.steelVersions.errors.inUseByTerminal],
    [800013, translation.steelVersions.errors.inUseByShuttle],
    [800014, translation.steelVersions.errors.inUseByAnchorMapping],
    [800015, translation.steelVersions.errors.inUseByConfigurationAnchorPoint],

    // installationTypes
    [900001, translation.installationTypes.errors.notFound],
    [900005, translation.installationTypes.errors.inUseByConfiguration],
    [900006, translation.installationTypes.errors.inUseByUpperExtension],
    [900007, translation.installationTypes.errors.inUseByLowerElement],
    [900008, translation.installationTypes.errors.inUseByIntermediateElement],
    [900009, translation.installationTypes.errors.inUseByLadderReinforcement],

    // normatives
    [1000001, translation.normatives.errors.notFound],
    [1000003, translation.normatives.errors.noDefined],
    [1000004, translation.normatives.errors.inUseByConfiguration],

    // items
    [1100001, translation.items.errors.notFound],
    [1100003, translation.items.errors.missingCode],

    // absorbers
    [1200001, translation.absorbers.errors.notFound],
    [1200002, translation.absorbers.errors.alreadyPresent],
    [1200003, translation.absorbers.errors.noDefined],
    [1200004, translation.absorbers.errors.inUseByConfiguration],

    // handles
    [1300001, translation.handles.errors.notFound],
    [1300002, translation.handles.errors.alreadyPresent],
    [1300003, translation.handles.errors.inUseByConfiguration],
    [1300004, translation.handles.errors.inUseByUpperExtension],

    // intermediateElements
    [1400001, translation.intermediateElements.errors.notFound],
    [1400002, translation.intermediateElements.errors.alreadyPresent],
    [1400003, translation.intermediateElements.errors.inUseByConfiguration],

    // ladderReinforcements
    [1500001, translation.ladderReinforcements.errors.notFound],
    [1500002, translation.ladderReinforcements.errors.alreadyPresent],
    [1500003, translation.ladderReinforcements.errors.inUseByConfiguration],

    // lowerElements
    [1600001, translation.lowerElements.errors.notFound],
    [1600002, translation.lowerElements.errors.alreadyPresent],
    [1600003, translation.lowerElements.errors.inUseByConfiguration],

    // shuttles
    [1700001, translation.shuttles.errors.notFound],
    [1700002, translation.shuttles.errors.alreadyPresent],
    [1700003, translation.shuttles.errors.inUseByConfiguration],

    // upperExtensions
    [1800001, translation.upperExtensions.errors.notFound],
    [1800002, translation.upperExtensions.errors.alreadyPresent],
    [1800003, translation.upperExtensions.errors.noDefined],
    [1800004, translation.upperExtensions.errors.inUseByConfiguration],

    // wallMaterialFixings
    [1900001, translation.wallMaterialFixings.errors.notFound],
    [1900004, translation.wallMaterialFixings.errors.inUseByConfiguration],

    // projectSharingInvites
    [6600003, translation.projects.errors.inviteUpdateNotAllowed],
    [6600005, translation.projects.errors.inviteAlreadyExists],
    [6600007, translation.projects.errors.inviteAlreadyCancelled],
    [6600008, translation.projects.errors.inviteAlreadyAccepted],
    [6600009, translation.projects.errors.inviteAlreadyRejected],

    [6500014, translation.lifeLines.errors.creatingCrossIntersectionsNotAllowed],
    [6500023, translation.lifeLines.errors.loopsNotAllowed],
    [6500024, translation.lifeLines.errors.intersectionsNotAllowed],

    // border

    //upright
    [6700007, translation.uprights.errors.alreadyPresent],
    [6700008, translation.common.fieldInvalidValue],
    [6700009, translation.common.fieldCannotBeEmpty],

    //guardrail compositions
    [6800001, translation.guardrailCompositions.errors.notFound],
    [6800002, translation.guardrailCompositions.errors.missingName],

    //toe boards
    [6900007, translation.toeBoards.errors.alreadyPresent],

    //bases
    [7100001, translation.bases.errors.notFound],
    [7100007, translation.bases.errors.alreadyPresent],

    // ballasts
    [7400001, translation.ballasts.errors.notFound],
    [7400007, translation.ballasts.errors.alreadyPresent],
    [7400011, translation.ballastTypes.errors.notFound],
    [7400012, translation.ballastTypes.errors.missingName],

    //membraneProtections
    [7500007, translation.membraneProtections.errors.alreadyPresent],

    //upright caps
    [7600007, translation.uprightCaps.errors.alreadyPresent],

    //upright types
    [8600001, translation.uprightTypes.errors.notFound],
    [8600002, translation.uprightTypes.errors.missingName],
    [8600007, translation.uprightTypes.errors.inUseByUprights],

    //rail caps
    [7700007, translation.railCaps.errors.alreadyPresent],

    //rail wall fixings
    [7800007, translation.railWallFixings.errors.alreadyPresent],

    //rail corner
    [7900007, translation.railCorners.errors.alreadyPresent],

    //gates
    [8000007, translation.gates.errors.alreadyPresent],

    //waterproofKits
    [8100007, translation.waterproofKits.errors.alreadyPresent],

    //fastening choices
    [8800001, translation.fasteningChoices.errors.notFound],
    [8800009, translation.fasteningChoices.errors.alreadyPresent],
    [8900009, translation.fasteningChoices.errors.duplicateFastening],

    //rails
    [8200007, translation.rails.errors.alreadyPresent],

    // systemNormative
    [8300008, translation.systemNormatives.errors.normativeAlreadyPresent],

    //uprightMapping
    [8400010, translation.uprightMappings.errors.alreadyPresent],

    //uprightSpanMapping
    [8500008, translation.uprightSpanMappings.errors.updateNotAllowed],
    [8500010, translation.uprightSpanMappings.errors.alreadyPresent],

    // entityAccesses
    [9100004, translation.entityAccesses.errors.alreadyExists],
  ]);

  return { translation: translation, errorsMap };
};

export default useI18n;
